import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Warning } from "@phosphor-icons/react";

/**
 * Sticky yellow disclaimer banner shown only when the signed-in user is the
 * public read-only demo visitor. Mounted once in App.js so it appears on
 * every protected page (Dashboard, Recipe Builder, Ingredients, etc.).
 *
 * Hidden for real users (paying customers + founder/admin).
 *
 * Also toggles `body.is-demo` so the global print CSS in App.css can
 * stamp a "DEMO — NOT FOR DISTRIBUTION" watermark over Ctrl+P output.
 */
export default function DemoBanner() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  // The public Compass demo is its own scoped experience; never apply the
  // retired Bench demo banner or its copy/edit restrictions there.
  const isDemo = user && (user.role === "demo" || user.is_demo) && !pathname.startsWith("/compass/demo");
  const lastToastRef = useRef(0);

  useEffect(() => {
    if (!isDemo) {
      document.body.classList.remove("is-demo");
      return undefined;
    }
    document.body.classList.add("is-demo");

    // Demo is look-only: block every path that lets a visitor lift the
    // formulation, safety plan, or any other proprietary content out of the
    // app — copy, cut, right-click, and drag-select. Browser printing is
    // already neutralized by the `body.is-demo` @media print rule in App.css.
    const notify = () => {
      const now = Date.now();
      if (now - lastToastRef.current > 2500) {
        lastToastRef.current = now;
        toast.info(
          "Copying is disabled in the demo. Create an account or sign in for full access.",
        );
      }
    };
    const blockCopy = (e) => {
      e.preventDefault();
      notify();
    };
    const blockSilently = (e) => e.preventDefault();

    document.addEventListener("copy", blockCopy, true);
    document.addEventListener("cut", blockCopy, true);
    document.addEventListener("contextmenu", blockSilently, true);
    document.addEventListener("dragstart", blockSilently, true);

    return () => {
      document.body.classList.remove("is-demo");
      document.removeEventListener("copy", blockCopy, true);
      document.removeEventListener("cut", blockCopy, true);
      document.removeEventListener("contextmenu", blockSilently, true);
      document.removeEventListener("dragstart", blockSilently, true);
    };
  }, [isDemo]);

  if (!isDemo) return null;

  return (
    <div
      className="sticky top-0 z-50 bg-amber-300 border-b-2 border-amber-500 text-amber-950"
      data-testid="demo-banner"
      role="alert"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[13px] font-medium">
          <Warning size={18} weight="fill" className="shrink-0" />
          <span>
            <b>Demo mode — read-only.</b>{" "}
            <span className="hidden sm:inline">
              You're exploring Alaine's Bench™ with public starter formulations.
              Saving, editing, and purchases are disabled.
            </span>
            <span className="sm:hidden">Saving is disabled.</span>
          </span>
        </div>
        <div className="flex items-center gap-3 text-[13px]">
          <button
            type="button"
            onClick={async () => {
              await logout();
              // Full reload to the landing page: in-app navigation races the
              // route guards (Paywalled -> /login, ComingSoon -> /dashboard),
              // so a hard redirect is the only reliable exit (same pattern as
              // the main Log out button in Layout.jsx).
              window.location.assign(`${process.env.PUBLIC_URL || ""}/`);
            }}
            className="text-[12px] text-amber-950/80 hover:text-amber-950 underline-offset-4 hover:underline"
            data-testid="demo-banner-exit-button"
          >
            Exit demo
          </button>
        </div>
      </div>
    </div>
  );
}
